import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./BlogsList.css";
import { toast } from "react-toastify";
import { deleteBlog, getBlogs } from "../../../api/blogs";
import { isBlogCoverFallback, resolveBlogCoverSrc } from "../../../utils/blogImages";
import { blogStatusLabel, isBlogPublished } from "../../../utils/blogStatus";
import { readingTimeLabelFromBlog } from "../../../utils/readingTime";
import {
    BookOpen,
    Calendar,
    Eye,
    FileEdit,
    Globe2,
    History,
    Layers,
    Pencil,
    PenSquare,
    Search,
    Timer,
    Trash2,
    UserRound,
} from "lucide-react";

function formatUpdatedChip(blog) {
    if (!blog?.updatedAt || !blog?.createdAt) return null;
    try {
        const u = new Date(blog.updatedAt).getTime();
        const c = new Date(blog.createdAt).getTime();
        if (Number.isNaN(u) || u <= c + 2000) return null;
        return new Date(blog.updatedAt).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return null;
    }
}

const BlogsList = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionError, setActionError] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState("newest");
    const [statusFilter, setStatusFilter] = useState("all");

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const data = await getBlogs();
                setBlogs(Array.isArray(data) ? data : []);
            } catch (err) {
                const msg =
                    err?.response?.data?.message ||
                    err?.message ||
                    "Failed to load blog posts. Please try again.";
                setError(msg);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    const handleDelete = async (blog) => {
        const ok = window.confirm(`Delete "${blog.title}"? This cannot be undone.`);
        if (!ok) return;

        setDeletingId(blog._id);
        setActionError("");
        try {
            await deleteBlog(blog._id);
            setBlogs((prev) => prev.filter((b) => b._id !== blog._id));
            toast.success("Blog deleted.");
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to delete blog. Please try again.";
            setActionError(msg);
        } finally {
            setDeletingId(null);
        }
    };

    const filteredBlogs = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = [...blogs];

        if (statusFilter === "draft") {
            list = list.filter((blog) => !isBlogPublished(blog));
        } else if (statusFilter === "published") {
            list = list.filter((blog) => isBlogPublished(blog));
        }

        if (q) {
            list = list.filter((blog) => {
                const haystack = `${blog?.title || ""} ${blog?.intro || ""} ${blog?.slug || ""} ${blog?.author || ""}`.toLowerCase();
                return haystack.includes(q);
            });
        }

        list.sort((a, b) => {
            if (sort === "title") {
                return String(a?.title || "").localeCompare(String(b?.title || ""));
            }
            const aTime = new Date(a?.createdAt || 0).getTime();
            const bTime = new Date(b?.createdAt || 0).getTime();
            return sort === "oldest" ? aTime - bTime : bTime - aTime;
        });

        return list;
    }, [blogs, query, sort, statusFilter]);

    const totalCount = blogs.length;
    const liveCount = useMemo(() => blogs.filter(isBlogPublished).length, [blogs]);
    const draftCount = useMemo(() => blogs.filter((b) => !isBlogPublished(b)).length, [blogs]);
    const shownCount = filteredBlogs.length;
    const isFiltering = query.trim().length > 0 || statusFilter !== "all";

    if (loading) {
        return (
            <section className="blglist" aria-label="Blog list loading">
                <div className="blogs-skel blogs-skel-pagehead" />
                <div className="blogs-skel blogs-skel-toolbar" />
                <div className="blogs-skel-grid">
                    <div className="blogs-skel blogs-skel-card" />
                    <div className="blogs-skel blogs-skel-card" />
                    <div className="blogs-skel blogs-skel-card" />
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="blglist" aria-label="Blog list error">
                <div className="blogs-alert blogs-alert--error" role="alert">
                    <BookOpen className="blogs-alert-icon" size={22} strokeWidth={2} aria-hidden />
                    <div>
                        <h2 className="blogs-alert-title">Could not load posts</h2>
                        <p className="blogs-alert-text">{error}</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="blglist">
            <header className="blogs-pagehead">
                <div className="blogs-pagehead-inner">
                    <div className="blogs-pagehead-main">
                        <p className="blogs-eyebrow">
                            <Layers size={15} strokeWidth={2} className="blogs-eyebrow-icon" aria-hidden />
                            Content library
                        </p>
                        <h1 className="blogs-h1">Blog posts</h1>
                    </div>
                    <Link className="blogs-cta-primary" to="/dashboard/add-blog">
                        <PenSquare size={18} strokeWidth={2} aria-hidden />
                        Create blog
                    </Link>
                </div>
            </header>

            {actionError ? (
                <div className="blogs-alert blogs-alert--error blogs-action-alert" role="alert">
                    <BookOpen className="blogs-alert-icon" size={22} strokeWidth={2} aria-hidden />
                    <div>
                        <h2 className="blogs-alert-title">Could not delete post</h2>
                        <p className="blogs-alert-text">{actionError}</p>
                    </div>
                </div>
            ) : null}

            <div className="blogs-toolbar">
                <div className="blogs-toolbar-inner">
                    <div className="blogs-field blogs-field--grow">
                        <label htmlFor="blogs-search" className="blogs-field-label">
                            <Search size={14} strokeWidth={2} aria-hidden />
                            Search
                        </label>
                        <div className="blogs-input-wrap">
                            <Search className="blogs-input-icon" size={17} strokeWidth={2} aria-hidden />
                            <input
                                id="blogs-search"
                                type="search"
                                className="blogs-input"
                                placeholder="Title, intro, slug, or author…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                autoComplete="off"
                                aria-label="Filter posts"
                            />
                            {query ? (
                                <button
                                    type="button"
                                    className="blogs-input-clear"
                                    onClick={() => setQuery("")}
                                    aria-label="Clear search"
                                >
                                    ×
                                </button>
                            ) : null}
                        </div>
                    </div>

                    <div className="blogs-field">
                        <label htmlFor="blogs-status" className="blogs-field-label">
                            Status
                        </label>
                        <select
                            id="blogs-status"
                            className="blogs-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All posts</option>
                            <option value="published">Published</option>
                            <option value="draft">Drafts</option>
                        </select>
                    </div>

                    <div className="blogs-field">
                        <label htmlFor="blogs-sort" className="blogs-field-label">
                            Sort
                        </label>
                        <select id="blogs-sort" className="blogs-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                            <option value="title">Title A–Z</option>
                        </select>
                    </div>

                    <div className="blogs-counts" aria-live="polite">
                        <span className="blogs-count-pill" title="Total posts">
                            {totalCount} total
                        </span>
                        <span className="blogs-count-pill" title="Published on public blog">
                            {liveCount} live
                        </span>
                        <span className="blogs-count-pill" title="Draft posts">
                            {draftCount} drafts
                        </span>
                        <span className="blogs-count-pill blogs-count-pill--accent" title="Matching filters">
                            {isFiltering ? `${shownCount} / ${totalCount}` : `${shownCount} shown`}
                        </span>
                    </div>
                </div>
            </div>

            {totalCount === 0 ? (
                <div className="blogs-empty">
                    <div className="blogs-empty-visual" aria-hidden>
                        <BookOpen size={36} strokeWidth={1.5} />
                    </div>
                    <h2 className="blogs-empty-title">No posts yet</h2>
                    <p className="blogs-empty-text">Create your first article to see it listed here.</p>
                    <Link className="blogs-cta-primary" to="/dashboard/add-blog">
                        <PenSquare size={18} strokeWidth={2} aria-hidden />
                        Create first blog
                    </Link>
                </div>
            ) : shownCount === 0 ? (
                <div className="blogs-empty">
                    <div className="blogs-empty-visual" aria-hidden>
                        <Search size={34} strokeWidth={1.5} />
                    </div>
                    <h2 className="blogs-empty-title">No matches</h2>
                    <p className="blogs-empty-text">Nothing matches “{query.trim()}”. Try another keyword.</p>
                    <button
                        type="button"
                        className="blogs-cta-ghost"
                        onClick={() => {
                            setQuery("");
                            setSort("newest");
                            setStatusFilter("all");
                        }}
                    >
                        Reset filters
                    </button>
                </div>
            ) : (
                <div className="blogs-grid">
                    {filteredBlogs.map((blog) => {
                        const updatedShort = formatUpdatedChip(blog);
                        return (
                        <article key={blog._id} className="blogs-card">
                            <div className="blogs-card-media">
                                <span
                                    className={`blogs-status-badge ${
                                        isBlogPublished(blog) ? "blogs-status-badge--live" : "blogs-status-badge--draft"
                                    }`}
                                >
                                    {isBlogPublished(blog) ? (
                                        <Globe2 size={12} strokeWidth={2} aria-hidden />
                                    ) : (
                                        <FileEdit size={12} strokeWidth={2} aria-hidden />
                                    )}
                                    {blogStatusLabel(blog)}
                                </span>
                                <img
                                    className={`blogs-card-img${isBlogCoverFallback(blog.image) ? " blogs-card-img--fallback" : ""}`}
                                    src={resolveBlogCoverSrc(blog.image)}
                                    alt=""
                                    loading="lazy"
                                />
                            </div>

                            <div className="blogs-card-main">
                                <h2 className="blogs-card-title">{blog.title}</h2>
                                <p className="blogs-card-intro">
                                    {blog.intro
                                        ? `${blog.intro.substring(0, 140)}${blog.intro.length > 140 ? "…" : ""}`
                                        : "No intro set."}
                                </p>

                                <div className="blogs-card-meta">
                                    <span className="blogs-meta-chip">
                                        <Calendar size={13} strokeWidth={2} aria-hidden />
                                        {blog.createdAt
                                            ? new Date(blog.createdAt).toLocaleDateString(undefined, {
                                                  year: "numeric",
                                                  month: "short",
                                                  day: "numeric",
                                              })
                                            : "—"}
                                    </span>
                                    {updatedShort ? (
                                        <span className="blogs-meta-chip blogs-meta-chip--soft" title="Last updated">
                                            <History size={13} strokeWidth={2} aria-hidden />
                                            Upd. {updatedShort}
                                        </span>
                                    ) : null}
                                    <span className="blogs-meta-chip">
                                        <Timer size={13} strokeWidth={2} aria-hidden />
                                        {readingTimeLabelFromBlog(blog)}
                                    </span>
                                    <span className="blogs-meta-chip">
                                        <UserRound size={13} strokeWidth={2} aria-hidden />
                                        {blog.author?.trim() || "Anonymous"}
                                    </span>
                                </div>
                            </div>

                            <div className="blogs-card-actions">
                                <Link to={`/dashboard/blog/${blog._id}`} className="blogs-btn blogs-btn--muted">
                                    <Eye size={15} strokeWidth={2} aria-hidden />
                                    View
                                </Link>
                                <Link to={`/dashboard/edit-blog/${blog._id}`} className="blogs-btn blogs-btn--primary">
                                    <Pencil size={15} strokeWidth={2} aria-hidden />
                                    Edit
                                </Link>
                                <button
                                    type="button"
                                    className="blogs-btn blogs-btn--danger"
                                    onClick={() => handleDelete(blog)}
                                    disabled={deletingId === blog._id}
                                >
                                    <Trash2 size={15} strokeWidth={2} aria-hidden />
                                    {deletingId === blog._id ? "…" : "Delete"}
                                </button>
                            </div>
                        </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

export default BlogsList;
